// Создано: 2026-07-03 — единая логика отображения сумм в billing/checkout.
// Tinkoff всегда списывает рубли; USD в записи price — каталожная валюта для конвертации.

const RUB_PAYMENT_GATEWAYS = new Set(['tinkoff', 'robokassa']);

export interface BillingDisplayMoney {
  amount: number;
  currency: string;
}

export interface ResolveBillingDisplayMoneyInput {
  amount: number;
  currency: string;
  paymentGateway?: string | null;
  localeCurrency?: string;
  currencyRate?: number;
}

export function getActiveRubPaymentGateway(billing?: {
  tinkoff?: {enable?: boolean};
  robokassa?: {enable?: boolean};
}): string | null {
  if (billing?.tinkoff?.enable) {
    return 'tinkoff';
  }
  if (billing?.robokassa?.enable) {
    return 'robokassa';
  }

  return null;
}

function normalizeCurrency(currency: string): string {
  return currency.trim().toUpperCase();
}

function convertUsdToCurrency(
  amount: number,
  targetCurrency: string,
  currencyRate: number,
): number {
  return Math.round(amount * currencyRate * 100) / 100;
}

/**
 * Возвращает сумму и валюту для отображения пользователю.
 * - RUB-цена показывается как есть.
 * - USD-цена конвертируется в валюту локализации, если задан курс.
 * - Оплата через Tinkoff всегда отображается в RUB.
 */
export function resolveBillingDisplayMoney(
  input: ResolveBillingDisplayMoneyInput,
): BillingDisplayMoney {
  const amount = input.amount;
  const currency = normalizeCurrency(input.currency || 'USD');
  const localeCurrency = input.localeCurrency
    ? normalizeCurrency(input.localeCurrency)
    : undefined;
  const gateway = input.paymentGateway?.toLowerCase() ?? null;
  const usesRubGateway = gateway != null && RUB_PAYMENT_GATEWAYS.has(gateway);

  if (usesRubGateway) {
    if (currency === 'RUB') {
      return {amount, currency: 'RUB'};
    }

    if (currency === 'USD' && input.currencyRate) {
      return {
        amount: convertUsdToCurrency(amount, 'RUB', input.currencyRate),
        currency: 'RUB',
      };
    }

    // Tinkoff списывает рубли даже если в price ошибочно указан USD.
    return {amount, currency: 'RUB'};
  }

  if (
    currency === 'USD' &&
    localeCurrency &&
    localeCurrency !== 'USD' &&
    input.currencyRate
  ) {
    return {
      amount: convertUsdToCurrency(amount, localeCurrency, input.currencyRate),
      currency: localeCurrency,
    };
  }

  return {amount, currency};
}
